import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckTwoToneIcon from "@mui/icons-material/CheckTwoTone";
import { Box, SvgIcon, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import Panel from "../Panel";
import { colors } from "../../config";

interface ProductValueProps {
  text?: string | null;
  arrowDown?: boolean;
}

export default function ProductValue({ text, arrowDown }: ProductValueProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [flag, setFlag] = useState(true);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleCopy = async () => {
    if (text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
    }
    setFlag(false);
    setTimeout(() => {
      setFlag(true);
    }, 500);
  };

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPanelOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isPanelOpen
      ) {
        setIsPanelOpen(false);
      }
    };

    if (isPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPanelOpen]);

  return (
    <Box
      ref={containerRef}
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ 
        width: "100%", 
        alignSelf: "center",
      }}
    >
      <Box
        ref={anchorRef}
        display="flex"
        alignItems="center"
        gap={0.25}
        sx={{
          position: "relative",
        }}
      >
        <Typography
          variant="body1"
          color={isHovered ? colors.accent.orange : colors.primary.main}
          component="span"
          sx={{
            lineHeight: 1.5,
            fontWeight: 400,
          }}
        >
          {text || "-"}
        </Typography>
        {arrowDown && text && (
          <SvgIcon
            component={
              isPanelOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon
            }
            onClick={handleIconClick}
            sx={{
              fontSize: "16px",
              color: colors.primary.main,
              cursor: "pointer",
              flexShrink: 0,
              "&:hover": {
                color: colors.accent.orange,
              },
            }}
          />
        )}
        {isHovered && text && (
          <SvgIcon
            component={flag ? ContentCopyIcon : CheckTwoToneIcon}
            onClick={handleCopy}
            sx={{
              position: "absolute",
              right: arrowDown ? "16px" : "0px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "18px",
              color: colors.primary.main,
              cursor: "pointer",
              zIndex: 1,
              backgroundColor: colors.background.white,
              padding: "2px",
              borderRadius: "2px",
            }}
          />
        )}
      </Box>
      <Panel isOpen={isPanelOpen} anchorElement={anchorRef.current} text={text} />
    </Box>
  );
}
